/**
 * SIL-473 — Circularity tab: THE ONE MODULE the tab's sample-tagged
 * fixtures live in, mirroring `environmentalSampleData.ts`'s convention for
 * the Overview tab. Per resources/plans/sil-461-dashboard-data-readiness.md,
 * prod had zero `marketplace_transaction` rows at the time — the resale leg
 * of the lifecycle Sankey and the avoided-vs-goal bar had no real source.
 *
 * SIL-471 wired the Sankey's resale leg (`SAMPLE_RESALE_LEG_ASSET_COUNT` /
 * `SAMPLE_RESALE_LEG_EMBODIED_KGCO2E` / `SAMPLE_RESALE_AVOIDED_VS_NEW_KGCO2E`
 * below) to real per-org data once `marketplace_transaction` gained
 * COMPLETED rows — these three constants are now the FALLBACK
 * `useLifecycleSankey` renders for an org with zero completed sales, not the
 * only render path; see `hooks/useEnvironmentalCircularity.ts` for the
 * real-vs-sample selection logic. The avoided-vs-goal bar still has no real
 * serving surface and stays sample-only.
 *
 * Everything else on this tab (the Sankey's repurpose/disposal legs, the
 * disposition-comparison panel) is REAL, served by
 * `GET /api/client/environmental/circularity` — see
 * `hooks/useEnvironmentalCircularity.ts`. Never import this module for
 * anything that route can serve.
 */
import type { EnvironmentalBasis } from '@siliconx/shared';

export const CIRCULARITY_SAMPLE_BASIS: EnvironmentalBasis = 'sample';

/**
 * Resale leg of the lifecycle Sankey — a plausible flow given the fleet's
 * real repurpose/disposal legs, NOT derived from any real transaction.
 * Scaled to sit alongside the real legs' typical magnitude so the diagram
 * reads honestly (a real leg's width is never dwarfed by an inflated
 * sample), not to make resale look larger than it is.
 */
export const SAMPLE_RESALE_LEG_ASSET_COUNT = 9;
export const SAMPLE_RESALE_LEG_EMBODIED_KGCO2E = 42_000;

/** Sankey resale-leg terminal annotation — a flat sample figure, same
 * convention as the Overview tab's SAMPLE_AVOIDED_KGCO2E (not arithmetically
 * derived from the sample leg above; both are independent sample fixtures). */
export const SAMPLE_RESALE_AVOIDED_VS_NEW_KGCO2E = 31_500;

/** Avoided-to-date vs org goal bar — both figures sample (advisory vs
 * advisory is the one allowed pairing per the SIL-459 register; neither
 * number is a footprint figure). */
export const SAMPLE_AVOIDED_TO_DATE_KGCO2E = 186_000;
export const SAMPLE_CIRCULARITY_GOAL_KGCO2E = 400_000;
